import { boardLink } from '../../tasks/utils/board-navigation';
import { NavLink, useSearchParams } from 'react-router-dom';

import { Notice } from '../../../common/components/detail/detail';
import { Gate } from '../../../global/contexts/auth/auth';
import TiersOverview from '../../tiers/layouts/overview';
import LiveIndicator from '../../live/layouts/live-indicator';
import TasksBoard from '../../tasks/layouts/board';
import TasksOverview from '../../tasks/layouts/overview';
import ReviewQueue from '../../reviews/layouts/queue';
import FindingsOverview from '../../findings/layouts/overview';

import './styles/development.css';

// Desarrollo: las tres caras del mismo trabajo.
//
// Tareas, Revisión y Hallazgos eran tres entradas del riel, y son tres momentos
// de una sola cosa — lo que hay por hacer, lo que está esperando un veredicto, y
// lo que apareció de costado mientras tanto. La prueba de que son uno: LAS TRES
// LEEN `GET /admin/tasks`, con distinto filtro. Revisión pide
// `workflow_state=in_review`; Hallazgos pide `proposed` y separa las que tienen
// `origin_task_id`.
//
// Y por eso el filtro de proyecto viaja entre pestañas: es el mismo `?project=`
// sobre la misma tabla, así que cambiar de pestaña con un proyecto elegido y
// perderlo sería volver a elegirlo tres veces para mirar lo mismo desde tres
// ángulos.
//
// El permiso es `tasks:read` para las tres: no hay código `findings:*` ni
// `reviews:*` en core/authz/roles.js, y un hallazgo ES una tarea.
//
// TIERS VA PRIMERO, porque va primero en el recorrido: un tier se parte en las
// tareas que las otras tres pestañas muestran. Es la única que no lee
// `/admin/tasks` sino `/admin/tiers`, y por eso trae su propio permiso,
// `tiers:read`: sin él la pestaña no se ofrece. La sección sigue abriendo en
// Tareas, que es lo que se mira todos los días. `?project=` le sirve igual: la
// lista de tiers filtra por el mismo parámetro.

const TABS = [
    { key: 'tiers',     title: 'Tiers', domain: 'tiers:read' },
    { key: 'tareas',    title: 'Tareas' },
    { key: 'revision',  title: 'Revisión' },
    { key: 'hallazgos', title: 'Hallazgos' },
];

export default function DevelopmentScreen({ tab = 'tareas' }) {
    const [params] = useSearchParams();

    // Preserve task context without mixing the different state/page filters.
    const shared = new URLSearchParams(params);
    for (const key of ['state', 'page', 'kind']) shared.delete(key);


    const current = TABS.some(item => item.key === tab) ? tab : 'tareas';

    return (
        <section className="development">
            <header className="development__header">
                <div className="development__heading">
                    <span className="development__eyebrow">Lo que está en curso</span>
                    <h2 className="development__title">Desarrollo</h2>
                </div>
                {/* The board, the review queue and the tiers keep themselves
                    up to date from the panel's live channel; this says whether
                    they are doing it right now. */}
                <LiveIndicator />
            </header>

            <nav className="development__tabs" aria-label="Secciones de desarrollo">
                {TABS.map(item => (
                    <Gate key={item.key} domain={item.domain}>
                        <NavLink
                            to={boardLink(`/admin/development/${item.key}`, shared)}
                            className={() => (
                                // Marked from `current` and not from NavLink's own
                                // isActive: /admin/development with no tab shows one,
                                // and NavLink would call none of them active.
                                `development__tab ${item.key === current ? 'development__tab--active' : ''}`
                            )}
                            aria-current={item.key === current ? 'page' : undefined}
                        >
                            {item.title}
                        </NavLink>
                    </Gate>
                ))}
            </nav>

            <div className="development__panel">
                {current === 'tiers' && (
                    // A link typed or shared by somebody with the permission
                    // still lands here: it says why, instead of an empty panel.
                    <Gate
                        domain="tiers:read"
                        fallback={<Notice>Hace falta <code>tiers:read</code> para ver los tiers.</Notice>}
                    >
                        <TiersOverview />
                    </Gate>
                )}
                {current === 'tareas' && <TasksTab />}
                {current === 'revision' && <ReviewQueue />}
                {current === 'hallazgos' && <FindingsOverview />}
            </div>
        </section>
    );
}

// El tablero y la lista son la misma consulta vista de dos formas, así que la
// elección vive en la URL —`?view=`— y no en estado local: se enlaza, vuelve con
// el botón Atrás, y conserva los filtros al alternar.
function TasksTab() {
    const [params, setParams] = useSearchParams();
    const view = params.get('view') === 'list' ? 'list' : 'board';

    const setView = (next) => {
        const target = new URLSearchParams(params);
        if (next === 'list') target.set('view', 'list');
        else target.delete('view');
        // La página es de la lista; el tablero no pagina.
        target.delete('page');
        setParams(target);
    };

    return (
        <>
            <div className="development__views" role="group" aria-label="Forma de ver las tareas">
                <button
                    type="button"
                    className={`development__view ${view === 'board' ? 'development__view--active' : ''}`}
                    onClick={() => setView('board')}
                    aria-pressed={view === 'board'}
                >
                    Tablero
                </button>
                <button
                    type="button"
                    className={`development__view ${view === 'list' ? 'development__view--active' : ''}`}
                    onClick={() => setView('list')}
                    aria-pressed={view === 'list'}
                >
                    Lista
                </button>
            </div>

            {view === 'board' ? <TasksBoard /> : <TasksOverview />}
        </>
    );
}
